// Round-trip memory for the Trades list, so returning from a trade-detail page
// restores the exact view the user left: the URL search (view-independent page
// / sort / filters), the grouped view's day-offset, and which session days were
// expanded. Scroll position is handled separately by the router's pathname-keyed
// scroll restoration (see router.tsx). State lives in sessionStorage so it is
// per-tab and cleared when the tab closes.

import type { TradeSortKey, TradeSortOrder } from "../api/hooks/useTrades";

export interface TradesListSearch {
  limit?: number;
  offset?: number;
  screen_id?: string;
  sort_key?: TradeSortKey;
  sort_order?: TradeSortOrder;
  since?: string;
  until?: string;
  from?: "calendar" | "dashboard";
  cal_y?: number;
  cal_m?: number;
}

export interface TradesListSnapshot {
  // The trades-list URL search to restore on "Back to trades".
  search: TradesListSearch;
  // Grouped view's session-day pagination offset (component state, not in URL).
  dayOffset: number;
  // Session keys (date or "__open__") that were expanded in the grouped view.
  expandedSessions: string[];
}

const SNAPSHOT_KEY = "trades:listState";
// One-shot flag set by "Back to trades" and consumed once by the Trades page so
// a fresh visit (e.g. clicking Trades in the sidebar) does not silently re-open
// the last session set — only an explicit return restores ephemeral state.
const RESTORE_FLAG_KEY = "trades:restore";

export function readTradesSnapshot(): TradesListSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SNAPSHOT_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as TradesListSnapshot;
    if (typeof parsed !== "object" || parsed === null) return null;
    return {
      search: parsed.search ?? {},
      dayOffset: typeof parsed.dayOffset === "number" ? parsed.dayOffset : 0,
      expandedSessions: Array.isArray(parsed.expandedSessions) ? parsed.expandedSessions : [],
    };
  } catch {
    return null;
  }
}

export function writeTradesSnapshot(snapshot: TradesListSnapshot): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // best-effort — persistence is non-critical
  }
}

// Signal that the next Trades-page mount should restore the saved ephemeral
// state. Set by the trade-detail "Back to trades" affordance.
export function markTradesRestore(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(RESTORE_FLAG_KEY, "1");
  } catch {
    // best-effort
  }
}

// Read-and-clear the restore flag. Returns true exactly once per "Back to
// trades" navigation.
export function consumeTradesRestore(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const flag = window.sessionStorage.getItem(RESTORE_FLAG_KEY) === "1";
    if (flag) window.sessionStorage.removeItem(RESTORE_FLAG_KEY);
    return flag;
  } catch {
    return false;
  }
}
